<script setup lang="ts">
import { computed, type Component } from 'vue';
import {
  IconBusiness,
  IconCalculator,
  IconCalendar,
  IconCheckbox,
  IconCube,
  IconDocumentText,
  IconHelp,
  IconList,
  IconReorder,
  IconText,
  IconUser,
} from '@/components/design/icons';
import { TemplateFieldType } from '@/api';
import {
  FIELD_TYPE_LABEL,
  FIELD_TYPE_TAG,
  type FlattenedField,
} from '@/utils/contractGeneration/templateSchemaFields';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import { gaiaFonts, gaiaNeutral, gaiaTemplating } from '@/components/design/themes/tokens';

/**
 * The top of the right pane on the Fields page: the selected field the way
 * the author knows it. A type-coloured glyph, the name as a title, the path
 * for those who read it, the facts the document already fixes (type,
 * required, default, options, formatting), and a bar underneath for where the
 * field appears — with the caller's controls in it (a pager in the Document
 * view, a way over to the document in the Fields view).
 */
interface Props {
  title: string;
  path: string;
  schema: FlattenedField | null;
}

const props = defineProps<Props>();

interface TypeLook {
  icon: Component;
  surface: string;
  fg: string;
}

/** The glyph tile follows the type chip's colour family so the two read as one. */
const TYPE_LOOK: Record<TemplateFieldType | 'unknown', TypeLook> = {
  string: { icon: IconText, surface: gaiaTemplating.objectSurface, fg: gaiaTemplating.objectFg },
  number: {
    icon: IconCalculator,
    surface: gaiaTemplating.objectSurface,
    fg: gaiaTemplating.objectFg,
  },
  date: {
    icon: IconCalendar,
    surface: gaiaTemplating.objectSurface,
    fg: gaiaTemplating.objectFg,
  },
  boolean: { icon: IconCheckbox, surface: gaiaNeutral.surfaceMuted, fg: gaiaNeutral.text },
  selection: {
    icon: IconList,
    surface: gaiaTemplating.entitySurface,
    fg: gaiaTemplating.entityFg,
  },
  object: { icon: IconCube, surface: gaiaTemplating.blockSurface, fg: gaiaTemplating.blockFg },
  list: {
    icon: IconReorder,
    surface: gaiaTemplating.blockSurface,
    fg: gaiaTemplating.blockFg,
  },
  legal_entity: {
    icon: IconBusiness,
    surface: gaiaTemplating.customSurface,
    fg: gaiaTemplating.customFg,
  },
  natural_person: {
    icon: IconUser,
    surface: gaiaTemplating.customSurface,
    fg: gaiaTemplating.customFg,
  },
  unknown: {
    icon: IconHelp,
    surface: gaiaNeutral.surfaceMuted,
    fg: gaiaNeutral.textSubtle,
  },
};

const look = computed<TypeLook>(() => TYPE_LOOK[props.schema?.type ?? 'unknown']);

const typeLabel = computed<string | null>(() => {
  const schema = props.schema;
  if (!schema) return null;
  const base = FIELD_TYPE_LABEL[schema.type] ?? schema.type;
  if (schema.type === TemplateFieldType.LIST && schema.itemType) {
    return `${base} of ${(FIELD_TYPE_LABEL[schema.itemType] ?? schema.itemType).toLowerCase()}`;
  }
  return base;
});

/** What the document already fixes about the value, as short facts with the detail behind a hover. */
const facts = computed<{ label: string; detail: string }[]>(() => {
  const schema = props.schema;
  if (!schema) return [];
  const items: { label: string; detail: string }[] = [];
  if (schema.defaultValue !== null) {
    items.push({ label: 'Has a default', detail: `Default: ${schema.defaultValue}` });
  }
  if (schema.selectionOptions.length > 0) {
    items.push({
      label: `${schema.selectionOptions.length} options`,
      detail: schema.selectionOptions.join(', '),
    });
  }
  if (schema.filters.length > 0) {
    items.push({ label: 'Formatted', detail: schema.filters.join(', ') });
  }
  return items;
});

// --- chrome ------------------------------------------------------------------
const monoFont = gaiaFonts.mono;
const divider = gaiaNeutral.borderDivider;
const barSurface = gaiaNeutral.surfaceSoft;
const barBorder = gaiaNeutral.borderCard;
</script>

<template>
  <!-- CSS carries what Naive has no prop for: the coloured glyph tile and the
       soft bar's surface/hairline; everything else is n-flex + tokens. -->
  <header class="inspector">
    <n-flex align="flex-start" :size="14" :wrap="false">
      <span
        class="inspector__tile"
        :style="{ background: look.surface, color: look.fg }"
        aria-hidden="true"
      >
        <n-icon :size="20"><component :is="look.icon" /></n-icon>
      </span>

      <n-flex vertical :size="4" :style="{ minWidth: 0, flex: '1 1 auto' }">
        <n-flex align="center" :size="8" :wrap="false" :style="{ minWidth: 0 }">
          <gaia-text truncate size="subtitle" weight="semibold" tone="ink">
            {{ props.title }}
          </gaia-text>
          <gaia-tag
            v-if="props.schema && typeLabel"
            size="small"
            round
            :type="FIELD_TYPE_TAG[props.schema.type] ?? 'default'"
          >
            {{ typeLabel }}
          </gaia-tag>
          <gaia-tag v-if="props.schema?.required" size="small" type="error" round
            >Required</gaia-tag
          >
        </n-flex>

        <n-flex align="center" :size="6" :wrap="false" :style="{ minWidth: 0 }">
          <gaia-text truncate size="micro" tone="subtle" :style="{ fontFamily: monoFont }">
            {{ props.path }}
          </gaia-text>
          <template v-for="fact in facts" :key="fact.label">
            <gaia-text size="micro" tone="faint" aria-hidden="true">·</gaia-text>
            <gaia-tooltip>
              <template #trigger>
                <gaia-text size="micro" tone="text" :style="{ whiteSpace: 'nowrap' }">
                  {{ fact.label }}
                </gaia-text>
              </template>
              {{ fact.detail }}
            </gaia-tooltip>
          </template>
          <gaia-text
            v-if="!props.schema"
            size="micro"
            tone="muted"
            :style="{ whiteSpace: 'nowrap' }"
          >
            · type details return once validation finishes
          </gaia-text>
        </n-flex>
      </n-flex>

      <n-flex
        v-if="$slots.actions"
        align="center"
        :size="6"
        :wrap="false"
        :style="{ flexShrink: 0 }"
      >
        <slot name="actions" />
      </n-flex>
    </n-flex>

    <!-- Where the field appears, and the controls for moving between places. -->
    <n-flex
      v-if="$slots.footer || $slots.aside"
      class="inspector__bar"
      align="center"
      justify="space-between"
      :size="10"
      :wrap="false"
    >
      <n-flex align="center" :size="8" :wrap="false" :style="{ minWidth: 0 }">
        <n-icon :size="15" :style="{ color: look.fg, flexShrink: 0 }">
          <icon-document-text />
        </n-icon>
        <gaia-text truncate size="dense" tone="text"><slot name="footer" /></gaia-text>
      </n-flex>
      <n-flex v-if="$slots.aside" align="center" :size="6" :wrap="false" :style="{ flexShrink: 0 }">
        <slot name="aside" />
      </n-flex>
    </n-flex>
  </header>
</template>

<style scoped>
.inspector {
  padding: 20px 22px 16px;
  border-bottom: 1px solid v-bind(divider);
}

.inspector__tile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  flex-shrink: 0;
}

.inspector__bar {
  margin-top: 14px;
  padding: 8px 12px;
  border: 1px solid v-bind(barBorder);
  border-radius: 10px;
  background: v-bind(barSurface);
}
</style>
